import { Link } from '@tanstack/react-router'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Sheet } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'
import { LanguageToggle } from '../components/LanguageToggle.tsx'

/**
 * Public page (no login), ADR 0038: how a resident or síndico connects their own Claude, ChatGPT, Claude Code or
 * Codex to the condo through MCP. It is the documentation URL for the directory listings, and the profile links here.
 * The server address comes from the page's own origin, so a domain change costs nothing.
 */
export function Assistants() {
  const mcpUrl = `${location.origin}/mcp`
  const copy = (text: string, what: string) => {
    void navigator.clipboard.writeText(text).then(() => toast.success(what), () => toast.error(t('Não foi possível copiar')))
  }
  const claudeCode = `claude mcp add --transport http subsindico-ia ${mcpUrl}`
  const codexAdd = `codex mcp add subsindico-ia --url ${mcpUrl}`
  const codexLogin = 'codex mcp login subsindico-ia'
  const json = JSON.stringify({ mcpServers: { 'subsindico-ia': { type: 'http', url: mcpUrl } } }, null, 2)
  return (
    <main className="mx-auto max-w-2xl px-5 py-10">
      {/* public pages reached from directory listings and links: the language can be switched here too (ADR 0040) */}
      <div className="flex items-center justify-between gap-3"><Link to="/" className="font-semibold text-caneta">{t('Subsíndico')}</Link><LanguageToggle signedIn={false} className="text-sm" /></div>
      <h1 className="mt-4 text-[1.8rem] font-bold leading-tight">{t('Conecte o seu assistente de IA ao condomínio')}</h1>
      <p className="mt-3 max-w-prose leading-relaxed text-tinta-2">
        {t('Se você usa o Claude ou o ChatGPT, eles podem consultar o que você já vê no Subsíndico: convenção, regimento, atas, avisos, reservas, prazos, finanças, portaria e consumo. O assistente é o seu; ele só enxerga o que o seu papel no condomínio permite, e você desconecta quando quiser.')}
      </p>

      <Sheet className="mt-6 p-5">
        <h2 className="font-bold">{t('Endereço do servidor')}</h2>
        <p className="mt-1 text-sm text-tinta-2">{t('Cole este endereço no seu assistente. O login é o mesmo do Subsíndico (código no WhatsApp ou chave de acesso), e você aprova a conexão numa página nossa.')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code id="mcp-url" className="min-w-0 flex-1 break-all rounded-ficha border border-pauta bg-papel px-3 py-2 font-doc text-[0.95rem]">{mcpUrl}</code>
          <Button variant="outline" onClick={() => copy(mcpUrl, t('Endereço'))}><Copy className="size-5" />{' '}{t('Copiar')}</Button>
        </div>
      </Sheet>

      <section className="mt-8 flex flex-col gap-6 leading-relaxed">
        <div>
          <h2 className="text-lg font-bold">{t('Claude (site, app de computador e celular)')}</h2>
          <ol className="mt-2 list-decimal pl-5">
            <li>{t('Abra')}{' '}<strong>{t('Configurações → Conectores')}</strong>{' '}{t('e escolha')}{' '}<strong>{t('Adicionar conector personalizado')}</strong>.</li>
            <li>{t('Dê um nome (por exemplo, “Condomínio”) e cole o endereço acima.')}</li>
            <li>{t('Clique em')}{' '}<strong>{t('Conectar')}</strong>{t(', entre no Subsíndico e toque em')}{' '}<strong>{t('Permitir')}</strong>.</li>
          </ol>
          <p className="mt-2 text-sm text-tinta-2">{t('No plano gratuito do Claude cabe um conector personalizado. Em contas Team ou Enterprise, quem administra a organização adiciona o conector e cada pessoa conecta a sua conta.')}</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('ChatGPT')}</h2>
          <ol className="mt-2 list-decimal pl-5">
            <li>{t('Abra')}{' '}<strong>{t('chatgpt.com/plugins')}</strong>{t(', toque em')}{' '}<strong>+</strong>{' '}{t('e escolha')}{' '}<strong>{t('Add custom MCP server')}</strong>.</li>
            <li>{t('Dê um nome, cole o endereço acima e deixe a autenticação em')}{' '}<strong>{t('OAuth')}</strong>.</li>
            <li>{t('Escolha')}{' '}<strong>{t('Create as a plugin')}</strong>{t(', instale, entre no Subsíndico e toque em')}{' '}<strong>{t('Permitir')}</strong>.</li>
            <li>{t('Numa conversa, digite')}{' '}<strong>@</strong>{' '}{t('e escolha o plugin.')}</li>
          </ol>
          <p className="mt-2 text-sm text-tinta-2">{t('A opção de adicionar servidores próprios depende do plano e das regras da sua conta ou do seu workspace.')}</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('Claude Code')}</h2>
          <p className="mt-1">{t('No terminal:')}</p>
          <Command text={claudeCode} onCopy={() => copy(claudeCode, t('Comando copiado'))} />
          <p className="mt-2">{t('Depois, dentro do Claude Code, rode')}{' '}<code className="font-doc">{t('/mcp')}</code>{t(', escolha')}{' '}<strong>{t('subsindico-ia')}</strong>{' '}{t('e autentique no navegador.')}</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('Codex (terminal, extensão da IDE e app)')}</h2>
          <Command text={codexAdd} onCopy={() => copy(codexAdd, t('Comando copiado'))} />
          <Command text={codexLogin} onCopy={() => copy(codexLogin, t('Comando copiado'))} />
          <p className="mt-2 text-sm text-tinta-2">{t('O segundo comando abre o navegador para você entrar no Subsíndico e permitir. A configuração vale para o terminal, a extensão e o app.')}</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('Cursor e outros clientes MCP')}</h2>
          <p className="mt-1">{t('Adicione ao arquivo de servidores MCP do seu cliente:')}</p>
          <Command text={json} onCopy={() => copy(json, t('Configuração'))} />
          <p className="mt-2 text-sm text-tinta-2">{t('Se o cliente não fizer login pelo navegador, crie um token em')}{' '}<strong>{t('Meu perfil → Assistentes de IA')}</strong>{' '}{t('e envie no cabeçalho')}{' '}<code className="font-doc">{t('Authorization: Bearer')}</code>.</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('O que o assistente pode fazer')}</h2>
          <ul className="mt-2 list-disc pl-5">
            <li>{t('Ler e buscar nos documentos que você pode ver, com o artigo e a página.')}</li>
            <li>{t('Listar avisos, assembleias, reservas, chamados, prazos, encomendas, funcionários e o resumo financeiro, conforme o seu papel.')}</li>
            <li>{t('Abrir um chamado, publicar um comunicado ou reservar um espaço, sempre com uma prévia antes e só se o seu papel permitir.')}</li>
          </ul>
          <p className="mt-2">{t('Exemplos: “quais são as regras do salão de festas?”, “o que foi decidido na última assembleia sobre a fachada?”, “quais prazos vencem este mês?”.')}</p>
        </div>

        <div>
          <h2 className="text-lg font-bold">{t('Privacidade e desconexão')}</h2>
          <p className="mt-1">
            {t('O que as ferramentas devolvem durante a conversa vai para o provedor do seu assistente (Anthropic, OpenAI ou outro), sob as regras da sua conta com ele. Para desconectar, revogue o acesso em')}{' '}<strong>{t('Meu perfil → Assistentes de IA')}</strong>{' '}{t('ou remova o conector no seu assistente. Mais detalhes em')}{' '}<Link to="/privacidade" className="font-semibold text-caneta">{t('Privacidade')}</Link>{' '}{t('e')}{' '}<Link to="/termos" className="font-semibold text-caneta">{t('Termos')}</Link>.
          </p>
        </div>
      </section>
    </main>
  )
}

function Command({ text, onCopy }: { text: string; onCopy: () => void }) {
  return (
    <div className="mt-2 flex items-start gap-2">
      <pre tabIndex={0} className="min-w-0 flex-1 overflow-x-auto rounded-ficha border border-pauta bg-papel p-3 font-doc text-[0.85rem]">{text}</pre>
      <Button variant="quiet" size="sm" onClick={onCopy} aria-label={t('Copiar')}><Copy className="size-5" /></Button>
    </div>
  )
}
